import { useEffect, useRef, useCallback } from 'react';

/**
 * Custom hook for WebSocket connections with auto-reconnect
 * 
 * @param {Object} options - Configuration options
 * @param {Function} options.onMessage - Callback for incoming messages
 * @param {Function} options.onConnect - Callback when connection established
 * @param {Function} options.onDisconnect - Callback when connection closed
 * @param {Function} options.onError - Callback for errors
 * @param {number} options.reconnectDelay - Delay before reconnecting (ms), default 5000
 * @param {boolean} options.enabled - Whether WebSocket should connect, default true
 * @returns {Object} { send, close, isConnected }
 */
const useWebSocket = ({
    onMessage,
    onConnect,
    onDisconnect,
    onError,
    reconnectDelay = 5000,
    enabled = true
} = {}) => {
    const wsRef = useRef(null);
    const reconnectTimeoutRef = useRef(null);
    const isConnectedRef = useRef(false);
    const shouldConnectRef = useRef(enabled);

    // Update shouldConnect when enabled prop changes
    useEffect(() => {
        shouldConnectRef.current = enabled;
        
        if (!enabled && wsRef.current) {
            wsRef.current.close();
            wsRef.current = null;
        } else if (enabled && !wsRef.current) {
            connectWebSocket();
        }
    }, [enabled]);

    const connectWebSocket = useCallback(() => {
        if (!shouldConnectRef.current) return;

        const token = localStorage.getItem('lipay_afya_token');
        if (!token) {
            console.log('No auth token available, skipping WebSocket connection');
            return;
        }

        try {
            // Get user ID from token
            const payload = JSON.parse(atob(token.split('.')[1]));
            const userId = payload.sub;
            
            // Use backend API URL (not frontend URL!)
            // For local dev: ws://localhost:8000
            // For production: wss://your-backend.com
            const isProduction = window.location.hostname !== 'localhost';
            const backendUrl = isProduction ? 'afya.lipay.store' : 'localhost:8000';
            const protocol = isProduction ? 'wss:' : 'ws:';
            const wsUrl = `${protocol}//${backendUrl}/user-messages/ws/${userId}?token=${token}`;
            
            console.log('Connecting to WebSocket:', wsUrl);
            const ws = new WebSocket(wsUrl);
            
            ws.onopen = () => {
                console.log('✅ WebSocket connected');
                isConnectedRef.current = true;
                
                if (onConnect) {
                    onConnect();
                }
                
                // Send ping to keep connection alive
                const pingInterval = setInterval(() => {
                    if (ws.readyState === WebSocket.OPEN) {
                        ws.send(JSON.stringify({ type: 'ping' }));
                    }
                }, 30000); // Ping every 30 seconds
                
                ws.pingInterval = pingInterval;
            };
            
            ws.onmessage = (event) => {
                try {
                    const message = JSON.parse(event.data);
                    
                    if (message.type === 'pong') {
                        // Pong received, connection is alive
                        return;
                    }
                    
                    if (onMessage) {
                        onMessage(message);
                    }
                } catch (error) {
                    console.error('Error parsing WebSocket message:', error);
                    if (onError) {
                        onError(error);
                    }
                }
            };
            
            ws.onerror = (error) => {
                console.error('WebSocket error:', error);
                isConnectedRef.current = false;
                
                if (onError) {
                    onError(error);
                }
            };
            
            ws.onclose = (event) => {
                console.log('WebSocket disconnected:', event.code, event.reason);
                isConnectedRef.current = false;
                
                // Clear ping interval
                if (ws.pingInterval) {
                    clearInterval(ws.pingInterval);
                }
                
                if (onDisconnect) {
                    onDisconnect();
                }
                
                // Attempt to reconnect if still enabled
                if (shouldConnectRef.current) {
                    console.log(`Reconnecting in ${reconnectDelay / 1000}s...`);
                    
                    if (reconnectTimeoutRef.current) {
                        clearTimeout(reconnectTimeoutRef.current);
                    }
                    
                    reconnectTimeoutRef.current = setTimeout(() => {
                        console.log('Attempting to reconnect WebSocket...');
                        connectWebSocket();
                    }, reconnectDelay);
                }
            };
            
            wsRef.current = ws;
        } catch (error) {
            console.error('Failed to create WebSocket connection:', error);
            if (onError) {
                onError(error);
            }
        }
    }, [onMessage, onConnect, onDisconnect, onError, reconnectDelay]);

    // Send message through WebSocket
    const send = useCallback((message) => {
        if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
            wsRef.current.send(JSON.stringify(message));
            return true;
        }
        console.warn('WebSocket is not connected, cannot send message');
        return false;
    }, []);

    // Close WebSocket connection
    const close = useCallback(() => {
        shouldConnectRef.current = false;
        
        if (reconnectTimeoutRef.current) {
            clearTimeout(reconnectTimeoutRef.current);
        }
        
        if (wsRef.current) {
            wsRef.current.close();
            wsRef.current = null;
        }
    }, []);

    // Initialize WebSocket on mount
    useEffect(() => {
        if (enabled) {
            connectWebSocket();
        }
        
        // Cleanup on unmount
        return () => {
            close();
        };
    }, [connectWebSocket, close, enabled]);

    return {
        send,
        close,
        isConnected: isConnectedRef.current
    };
};

export default useWebSocket;

